import { getPlayablePosts } from "@/lib/socialMedia.functions";

/** Playable sources already resolved this session, so scrolling back is instant. */
export const resolved = new Map<string, string>();
export const failedOnce = new Set<string>();
const inFlight = new Set<string>();

type NetworkInfo = { saveData?: boolean; effectiveType?: string };

/**
 * Phones and slow links should not pull desktop-sized opening chunks.
 * Read once: this does not change during a session in any way we care about.
 */
export const connectionProfile = (() => {
  if (typeof window === "undefined") {
    return { small: false, saveData: false };
  }
  const info = (navigator as Navigator & { connection?: NetworkInfo }).connection;
  const saveData = info?.saveData === true;
  const slow = /^(slow-2g|2g|3g)$/.test(info?.effectiveType ?? "");
  const phone = window.innerWidth < 768;
  return { small: saveData || slow || phone, saveData };
})();

export const playbackUrl = (postId: string) =>
  connectionProfile.small
    ? `/api/public/social-media/${postId}?chunk=small`
    : `/api/public/social-media/${postId}`;

/**
 * Resolves several posts at once so the next videos are ready before you get
 * to them.
 */
export async function primePlayable(postIds: string[]): Promise<void> {
  const wanted = postIds.filter(
    (id) => !resolved.has(id) && !failedOnce.has(id) && !inFlight.has(id),
  );
  if (wanted.length === 0) return;
  wanted.forEach((id) => inFlight.add(id));
  try {
    const { results } = await getPlayablePosts({ data: { postIds: wanted } });
    for (const item of results) {
      if (item.mediaUrl) resolved.set(item.postId, playbackUrl(item.postId));
      else failedOnce.add(item.postId);
    }
  } catch {
    wanted.forEach((id) => failedOnce.add(id));
  } finally {
    wanted.forEach((id) => inFlight.delete(id));
  }
}

/** Pulls the opening bytes so playback can start straight away. */
export function warmBytes(postId: string) {
  // Data saver: never spend the visitor's allowance on a video they may skip.
  if (connectionProfile.saveData) return;
  const range = connectionProfile.small ? "bytes=0-262143" : "bytes=0-524287";
  void fetch(playbackUrl(postId), { headers: { range } }).catch(() => undefined);
}
